import { LocaleDto, QueryExprSyntaxTree, UserRole } from 'src/app/services/api.services';
import { isNullOrUndefined, TimeZone } from 'src/app/shared/utils';
import { DataFormatter } from '../../formatting/DataFormatter';
import * as DataQueryExpr from '../expr';
import { VM } from './VM';
import { DataType } from '../../dataModel/DataType';
import { DataDisplayOptions, DataFormat } from '../../dataModel';
import { Value } from './Value';
import { Compiler } from './Compiler';
import { AppCurrency } from 'src/app/state/app/app.reducer';

export const ERROR = {};

export enum EvaluationOutput {
  /** Output formatted value. This is the default output, when unspecified. */
  FormattedValue,

  /** Output formatted value, transformed into text for display. */
  DisplayText,

  /** Ouptut unformatted value. */
  Raw,
}

export type EvaluatorOptions = {
  throwOnError?: boolean;
  errorResult?: any;

  /**
   * Whether to treat parse errors the same as evaluation errors.
   * By default, parse errors are always thrown, regardless of @see throwOnError.
   * When true, parse errors will be treated same as evaluation errors, i.e.
   * if @see throwOnError is false, evaluation will result in @errorResult instead of throwing.
   **/
  treatParseErrorAsEvaluationError?: boolean;

  /**
   * Whether to evaluate @errorResult
   * Note - the value will only be evaluated if
   * it is a @see QueryExprSyntaxTree or @see DataQueryExpr.SyntaxNode
   **/
  evaluateErrorResult?: boolean;

  /**
   * What sort of output to get from the evaluation.
   **/
  output?: EvaluationOutput;

  /**
   * Format of the output data.
   * When not provided, defaults for the given data type will be used.
   **/
  format?: DataFormat;

  /**
   * Display options for the output data.
   * When not provided, defaults for the given data type will be used.
   * Only applicable when evaluating for display.
   * To get display of a value without evaluation, see @see DataFormatter
   **/
  display?: DataDisplayOptions;
};

export namespace EvaluatorOptions {
  export function returnOnError(errorResult: any, evaluateErrorResult = true): EvaluatorOptions {
    return {
      throwOnError: false,
      errorResult,
      evaluateErrorResult,
      treatParseErrorAsEvaluationError: true
    };
  }

  export const returnERROR = returnOnError(ERROR, false);
  export const returnUndefinedOnError = returnOnError(undefined, false);
  export const returnNullOnError = returnOnError(null, false);
}

export type EvaluateInput = string | QueryExprSyntaxTree | DataQueryExpr.SyntaxNode;

export class EvaluationError extends Error {
  constructor(message?: string, options?: ErrorOptions & {
    syntax?: string | DataQueryExpr.SyntaxNode;
    location?: DataQueryExpr.TextLocation;
    parseError?: DataQueryExpr.ParseError;
  }) {
    super(message, options);
  }
}

export class Evaluator {
  constructor(
    private readonly contextOrFactory: (Evaluator.Context | (() => Evaluator.Context)),
    private readonly dataFormatterContext: DataFormatter.Context
  ) { }


  private depth = 0;

  context: Evaluator.Context;
  dataFormatter: DataFormatter;

  readonly vm = new VM(this);


  /**
   * Evaluates the specified input (syntax string, tree or node).
   **/
  eval(input: EvaluateInput, options?: EvaluatorOptions): any {
    this.depth++;

    try {
      if (this.depth == 1) {
        this.context = 
          typeof this.contextOrFactory === 'function'
            ? this.contextOrFactory()
            : this.contextOrFactory;

        this.dataFormatter = new DataFormatter(this.dataFormatterContext, this);
      }


      options ??= {
        throwOnError: true,
        output: EvaluationOutput.FormattedValue
      };

      let syntax = this.tryGetSyntaxNode(input);
      if (syntax === ERROR) {
        if (!options.throwOnError)
          return ERROR;

        throw new Error(`Invalid value '${typeof input}' passed to eval`, { cause: syntax });
      }

      try {
        let v = this.evalSyntax(syntax, options);

        let [value, dataType, isAggregated, aggregation] = 
          (v instanceof Value)
            ? [v.value, v.type, v.isAggregated, v.aggregation]
            : [v, null, false, null];

        if (options.output >= EvaluationOutput.FormattedValue)
          value = this.dataFormatter.applyDataFormat(value, { dataType, isAggregated, aggregation });

        if (options.output >= EvaluationOutput.DisplayText)
          value = this.dataFormatter.formatForDisplay(value, { dataType, isAggregated, aggregation });

        return value;
      }
      catch (err) {
        if (err instanceof EvaluationError) {
          if (options.throwOnError)
            throw err;

          let result = options.errorResult;

          if (options.evaluateErrorResult) {
            result = this.eval(result, EvaluatorOptions.returnERROR);
          }
        }
      }
    }
    finally {
      this.depth--;

      if (this.depth == 0) {
        this.context = null;
        this.dataFormatter = null;
      }
    }
  }

  /**
   * Evaluates the specified input (syntax string, tree or node).
   * If evaluation fails, evaluates the fallback.
   * If the fallback fails, throws an error.
   * Same as calling eval(input, EvaluatorOptions.returnOnError(fallback, true)).
   **/
  evalWithFallback(input: EvaluateInput, fallback: EvaluateInput) {
    return this.eval(input, EvaluatorOptions.returnOnError(fallback, true));
  }


  private parseSyntax(syntax: string, options: EvaluatorOptions): DataQueryExpr.SyntaxNode {
    try {
      return DataQueryExpr.parse(syntax);
    }
    catch (err) {
      if (err instanceof DataQueryExpr.ParseError) {
        if (options.treatParseErrorAsEvaluationError) {
          throw new EvaluationError(
            err.message ?? 'Parsing error',
            {
              cause: err.cause,
              syntax,
              location: err.location,
              parseError: err
            });
        } else {
          throw err;
        }
      }
    }
  }

  private evalSyntax(syntax: DataQueryExpr.SyntaxNode, options: EvaluatorOptions): any {
    // TODO: cache compiled syntax
    return Compiler.compile(syntax)(this.vm);
  }


  tryGetSyntaxNode(input: EvaluateInput): DataQueryExpr.SyntaxNode {
    if (input instanceof DataQueryExpr.SyntaxNode)
      return input;

    if (typeof input === 'string')
      return this.parseSyntax(input, EvaluatorOptions.returnERROR);

    if (typeof input === 'object' && 'root' in input) {
      if (!isNullOrUndefined(input.root)) {
        if (input.root instanceof DataQueryExpr.SyntaxNode) 
          return input.root;
        else 
          return DataQueryExpr.parse(input.root);
      }
    }

    return <any>ERROR;
  }
}

export namespace Evaluator {
  export interface Context {
    readonly locale: LocaleDto;
    readonly currency?: AppCurrency;

    readonly organisation: string;

    readonly timestamp: DataQueryExpr.DateTimeValue;
    readonly timestampUtc: DataQueryExpr.DateTimeValue;
    readonly tz: Readonly<TimeZone>;

    readonly user?: {
      readonly name: string;
      readonly role: UserRole;
      readonly isImpersonating: boolean;

      readonly timestamp: DataQueryExpr.DateTimeValue;
      readonly timestampUtc: DataQueryExpr.DateTimeValue;
      readonly tz: Readonly<TimeZone>;
    };

    readonly visualData?: {
      readonly name?: string;
    };

    readonly parameterResolver?: (name: string) => any;
  }
}

